import { StandardEvents } from '@shopify/events';

const MOBILE = window.matchMedia('(max-width: 767px)');

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
// Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
const T = (k, f) => D(window.C365_T && window.C365_T[k]) || f;

class C365Header extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-panel]');
    this.scrim = this.querySelector('[data-scrim]');
    this.results = this.querySelector('[data-panel-results]');
    this.empty = this.querySelector('[data-panel-empty]');
    this.inputs = Array.from(this.querySelectorAll('[data-search-input]'));

    this.bindMega();
    this.bindHelp();
    this.bindSearch();
    this.bindDrawer();
    this.bindSticky();
    this.bindCart();
  }

  /* ---------- mega menu: hover (150 ms intent), or click / tap on touch screens ---------- */
  bindMega() {
    const items = Array.from(this.querySelectorAll('[data-mega]'));
    const nav = this.querySelector('[data-nav]');
    const isOpen = (item) => item.classList.contains('is-open');
    const close = () =>
      items.forEach((i) => {
        i.classList.remove('is-open');
        i.querySelector('.c365-hdr-mega').hidden = true;
        i.querySelector('a').setAttribute('aria-expanded', 'false');
      });
    const open = (item) => {
      if (isOpen(item)) return;
      close();
      item.classList.add('is-open');
      item.querySelector('.c365-hdr-mega').hidden = false;
      item.querySelector('a').setAttribute('aria-expanded', 'true');
    };
    let timer;
    this.querySelectorAll('.c365-hdr-nav__item').forEach((item) => {
      item.addEventListener('mouseenter', () => {
        clearTimeout(timer);
        timer = setTimeout(() => (item.hasAttribute('data-mega') ? open(item) : close()), 150);
      });
      item.addEventListener('focusin', () => item.hasAttribute('data-mega') && open(item));
    });
    nav?.addEventListener('mouseleave', () => {
      clearTimeout(timer);
      timer = setTimeout(close, 150);
    });
    // a tap (no hover) opens the panel first; a second tap follows the link
    items.forEach((item) =>
      item.querySelector('a').addEventListener('click', (e) => {
        if (window.matchMedia('(hover: hover)').matches || isOpen(item)) return;
        e.preventDefault();
        open(item);
      })
    );
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    document.addEventListener('click', (e) => !nav?.contains(e.target) && close());
  }

  /* ---------- Help dropdown ---------- */
  bindHelp() {
    const box = this.querySelector('[data-help]');
    if (!box) return;
    const btn = box.querySelector('[data-help-btn]');
    const menu = box.querySelector('[data-help-menu]');
    const set = (open) => {
      menu.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    };
    btn.addEventListener('click', () => set(menu.hidden));
    document.addEventListener('click', (e) => !box.contains(e.target) && set(false));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && set(false));
  }

  /* ---------- sticky header ----------
     Desktop: past the header, the top row comes back fixed as a compact bar; scrolling up shows the nav row too.
     The outer box keeps the header's height so the page doesn't jump. Mobile: the header group is sticky with the
     announcement bar scrolling away (CSS). Either way the visible height goes to --header-height (product page buy
     box) and --c365-header-stuck (wall art filter bars). */
  bindSticky() {
    const desk = this.querySelector('[data-desk]');
    const wrap = this.querySelector('[data-desk-wrap]');
    const group = this.closest('#header-group') || this;
    const bar = this.querySelector('.c365-hdr-bar');
    const setBar = () => document.documentElement.style.setProperty('--c365-bar-h', `${bar?.offsetHeight || 0}px`);
    setBar();
    if (bar && 'ResizeObserver' in window) new ResizeObserver(setBar).observe(bar);
    const setVar = (px) => {
      const v = `${Math.round(px)}px`;
      document.body.style.setProperty('--header-height', v);
      document.documentElement.style.setProperty('--c365-header-stuck', v);
    };
    // Horizon scrolls inside .page-wrapper (overflow: auto) rather than the window
    const scroller = this.scroller();
    const getY = () => (scroller ? scroller.scrollTop : window.scrollY);
    let lastY = getY();
    let full = 0;
    const update = () => {
      const y = getY();
      const up = y < lastY;
      lastY = y;
      if (MOBILE.matches) {
        this.classList.remove('is-stuck', 'is-stuck-up');
        desk.style.height = '';
        // the sticky group's bottom edge = what it covers (bar, plus the search field when open)
        setVar(Math.max(0, group.getBoundingClientRect().bottom));
        return;
      }
      if (!this.classList.contains('is-stuck')) full = desk.offsetHeight;
      const top = desk.getBoundingClientRect().top - (scroller ? scroller.getBoundingClientRect().top : 0) + y;
      const stuck = y > top + full;
      if (stuck) desk.style.height = `${full}px`;
      else desk.style.height = '';
      this.classList.toggle('is-stuck', stuck);
      this.classList.toggle('is-stuck-up', stuck && up);
      setVar(stuck ? wrap.getBoundingClientRect().height : 0);
    };
    (scroller || window).addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /** The element the page scrolls in: Horizon's .page-wrapper when it scrolls itself, else null (the window). */
  scroller() {
    const pw = document.querySelector('.page-wrapper');
    return pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) ? pw : null;
  }

  /** Stops the page behind an open drawer from scrolling (the window or .page-wrapper). */
  lockScroll(on) {
    document.documentElement.style.overflow = on ? 'hidden' : '';
    const pw = on ? this.scroller() : this._locked;
    if (pw) pw.style.overflow = on ? 'hidden' : '';
    this._locked = on ? pw : null;
  }

  /* ---------- predictive search ---------- */
  bindSearch() {
    const toggle = this.querySelector('[data-search-toggle]');
    const mSearch = this.querySelector('[data-m-search]');
    toggle?.addEventListener('click', () => {
      const show = mSearch.hidden;
      mSearch.hidden = !show;
      toggle.setAttribute('aria-expanded', String(show));
      if (show) {
        mSearch.querySelector('input').focus();
        this.openPanel();
      } else this.closePanel();
    });

    this.inputs.forEach((input) => {
      const clear = input.parentElement.querySelector('[data-search-clear]');
      input.addEventListener('focus', () => this.openPanel());
      input.addEventListener('input', () => {
        clear.hidden = !input.value;
        this.inputs.forEach((o) => o !== input && (o.value = input.value));
        clearTimeout(this._t);
        this._t = setTimeout(() => this.query(input.value), 180);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closePanel();
          input.blur();
        }
      });
      clear.addEventListener('click', () => {
        input.value = '';
        clear.hidden = true;
        input.focus();
        this.query('');
      });
    });

    this.scrim.addEventListener('click', () => this.closePanel());
    this.panel.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-term]');
      if (!chip) return;
      const input = this.visibleInput();
      input.value = chip.dataset.term;
      input.dispatchEvent(new Event('input'));
      input.focus();
    });
    document.addEventListener('click', (e) => {
      if (!this.panel.hidden && !this.contains(e.target)) this.closePanel();
    });
  }

  visibleInput() {
    return this.inputs.find((i) => i.offsetParent !== null) || this.inputs[0];
  }

  openPanel() {
    this.panel.hidden = false;
    this.scrim.hidden = false;
  }

  closePanel() {
    this.panel.hidden = true;
    this.scrim.hidden = true;
  }

  money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const s = n % 1 === 0 ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2 });
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, s);
  }

  async query(q) {
    const term = q.trim();
    if (!term) {
      this.results.hidden = true;
      this.empty.hidden = false;
      return;
    }
    // Fetch up to 10 products so suggestions can be derived from more titles; only 4 are shown.
    const url = `${this.dataset.predictiveUrl}.json?q=${encodeURIComponent(term)}&resources[type]=product,collection,query&resources[limit]=10&resources[options][unavailable_products]=last`;
    let data;
    try {
      data = await fetch(url).then((r) => r.json());
    } catch {
      return;
    }
    if (this.visibleInput().value.trim() !== term) return;
    const res = data?.resources?.results || {};
    const products = res.products || [];
    const collections = (res.collections || []).slice(0, 4);
    const queries = res.queries || [];
    const searchHref = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&options[prefix]=last`;

    this.empty.hidden = true;
    this.results.hidden = false;

    if (!products.length && !collections.length) {
      const chips = Array.from(this.empty.querySelectorAll('[data-term]'))
        .map((c) => `<button type="button" class="c365-hdr-chip" data-term="${escapeHtml(c.dataset.term)}">${escapeHtml(c.dataset.term)}</button>`)
        .join('');
      this.results.innerHTML = `<div class="c365-hdr-none"><b>${escapeHtml(T('header.no_results', 'No results for “__TERM__”')).replace('__TERM__', escapeHtml(term))}</b><span>${escapeHtml(T('header.no_results_hint', 'Check the spelling or try one of these:'))}</span><div class="c365-hdr-chips">${chips}</div></div>`;
      return;
    }

    // Suggestions: Shopify's query suggestions first, then short phrases taken from matching product titles
    // ("Custom 1st Birthday Photo Collage Canvas" → "1st birthday photo collage"), first word of the query in bold.
    const first = term.toLowerCase().split(/\s+/)[0];
    const derived = products.map((p) =>
      p.title
        .toLowerCase()
        .replace(/^custom\s+|\s*(on canvas|canvas print|canvas gift|canvas|[–|-]\s.*)$/g, '')
        .trim()
        .split(/\s+/)
        .slice(0, 4)
        .join(' ')
    );
    const terms = [...new Set([...queries.map((s) => s.text.toLowerCase()), ...derived])].filter(Boolean).slice(0, 5);
    const mark = (t) => {
      const k = t.indexOf(first);
      if (k < 0) return escapeHtml(t);
      return `${escapeHtml(t.slice(0, k))}<b>${escapeHtml(t.slice(k, k + first.length))}</b>${escapeHtml(t.slice(k + first.length))}`;
    };
    const sugg = terms
      .map(
        (t) =>
          `<button type="button" class="c365-hdr-sugg" data-term="${escapeHtml(t)}"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg><span>${mark(t)}</span></button>`
      )
      .join('');
    const popCols = this.querySelector('[data-pop-cols]')?.innerHTML.trim() || '';
    const cols = collections
      .map(
        (c) =>
          `<a class="c365-hdr-col" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg></a>`
      )
      .join('');
    const prods = products
      .slice(0, 4)
      .map((p) => {
        const img = p.featured_image?.url || p.image;
        const price = Math.round(parseFloat(p.price) * 100);
        const was = p.compare_at_price_max ? Math.round(parseFloat(p.compare_at_price_max) * 100) : 0;
        return `<a class="c365-hdr-prod" href="${escapeHtml(p.url)}"><div class="c365-hdr-prod__img">${img ? `<img src="${escapeHtml(img)}${img.includes('?') ? '&' : '?'}width=300" alt="" loading="lazy">` : ''}</div><span class="c365-hdr-prod__meta"><span class="c365-hdr-prod__name">${escapeHtml(p.title)}</span><span class="c365-hdr-prod__price"><b>${this.money(price)}</b>${was > price ? `<s>${this.money(was)}</s>` : ''}</span></span></a>`;
      })
      .join('');

    this.results.innerHTML = `
      <div class="c365-hdr-res">
        <div class="c365-hdr-res__side">
          ${sugg ? `<div class="c365-hdr-res__list"><span class="c365-hdr-kicker">${escapeHtml(T('header.suggestions', 'Suggestions'))}</span>${sugg}</div>` : ''}
          ${cols ? `<div class="c365-hdr-res__list"><span class="c365-hdr-kicker">${escapeHtml(T('header.collections', 'Collections'))}</span>${cols}</div>` : popCols ? `<div class="c365-hdr-res__list"><span class="c365-hdr-kicker">${escapeHtml(T('header.popular_collections', 'Popular collections'))}</span>${popCols}</div>` : ''}
        </div>
        <div class="c365-hdr-prods"><span class="c365-hdr-kicker">${escapeHtml(T('header.products', 'Products'))}</span><div class="c365-hdr-prods__grid">${prods}</div></div>
      </div>
      <a class="c365-hdr-all" href="${escapeHtml(searchHref)}">See all results for “${escapeHtml(term)}”<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></a>`;
  }

  /* ---------- mobile drawer: 2 levels, opens under the header, the menu button turns into × ---------- */
  bindDrawer() {
    const drawer = this.querySelector('[data-drawer]');
    const panel = this.querySelector('[data-drawer-panel]');
    const openBtn = this.querySelector('[data-drawer-open]');
    const views = Array.from(this.querySelectorAll('[data-dview]'));
    const root = views.find((v) => v.dataset.dview === '0');
    let rootScroll = 0;
    const show = (id) => {
      if (id === '0') {
        views.forEach((v) => (v.hidden = v !== root));
        panel.scrollTop = rootScroll;
        this.querySelectorAll('[data-dgo]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        return;
      }
      rootScroll = panel.scrollTop;
      views.forEach((v) => (v.hidden = v.dataset.dview !== id));
      panel.scrollTop = 0;
      this.querySelector(`[data-dgo="${id}"]`)?.setAttribute('aria-expanded', 'true');
      views.find((v) => v.dataset.dview === id)?.querySelector('[data-dback]')?.focus();
    };
    const close = () => {
      drawer.hidden = true;
      this.classList.remove('is-drawer-open');
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.setAttribute('aria-label', openBtn.dataset.labelOpen || openBtn.getAttribute('aria-label'));
      this.lockScroll(false);
      show('0');
    };
    const open = () => {
      // the drawer starts under the visible header
      drawer.style.top = `${Math.max(0, this.querySelector('.c365-hdr-m').getBoundingClientRect().bottom)}px`;
      drawer.hidden = false;
      this.classList.add('is-drawer-open');
      openBtn.setAttribute('aria-expanded', 'true');
      if (openBtn.dataset.labelClose) openBtn.setAttribute('aria-label', openBtn.dataset.labelClose);
      this.lockScroll(true);
      this.closePanel();
    };
    openBtn.addEventListener('click', () => (drawer.hidden ? open() : close()));
    drawer.addEventListener('click', (e) => e.target === drawer && close());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && !drawer.hidden && close());
    MOBILE.addEventListener('change', (e) => !e.matches && close());
    this.querySelectorAll('[data-dgo]').forEach((btn) => btn.addEventListener('click', () => show(btn.dataset.dgo)));
    this.querySelectorAll('[data-dback]').forEach((btn) => btn.addEventListener('click', () => show('0')));
    // swipe right on a sub level goes back
    let x0 = null;
    panel.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
    panel.addEventListener('touchend', (e) => {
      if (x0 !== null && e.changedTouches[0].clientX - x0 > 70 && root.hidden) show('0');
      x0 = null;
    });
  }

  /* ---------- cart badge ---------- */
  bindCart() {
    const render = (n) =>
      this.querySelectorAll('[data-cart-count]').forEach((b) => {
        b.textContent = String(n);
        b.hidden = !n;
      });
    // the cart icon opens the cart drawer where the page has one (sections/c365-cart-drawer.liquid); on the
    // cart page, or without the drawer's script, it is the plain link to the cart
    this.querySelectorAll('[data-cart-open]').forEach((link) =>
      link.addEventListener('click', (e) => {
        const drawer = document.querySelector('c365-cart-drawer');
        if (typeof drawer?.open !== 'function') return;
        e.preventDefault();
        drawer.open();
      })
    );
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise
        ?.then(({ cart, detail }) => render(cart?.totalQuantity ?? detail?.itemCount ?? 0))
        .catch(() => {});
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) fetch('/cart.js').then((r) => r.json()).then((c) => render(c.item_count)).catch(() => {});
    });
  }
}

if (!customElements.get('c365-header')) customElements.define('c365-header', C365Header);
